import { Component, Input, OnInit } from '@angular/core';
import { Platform } from '@ionic/angular';
import { Article } from 'src/app/_interfaces/article';
import { Attachment, AttachmentArticle } from 'src/app/_interfaces/Attachment';
import { HttpFunctionResult } from 'src/app/_interfaces/HttpFunctionResult';
import { ArticleService } from 'src/app/_provider/article.service';
import { AuthService } from 'src/app/_provider/auth.service';
import { AFSActionsheetButton, FeedbackService, MsgType } from 'src/app/_provider/feedback.service';
import { FilesService } from 'src/app/_provider/files.service';
import { TicketEmployee } from 'src/app/_provider/tickets/ticket.service';
import { PopoverListItem, PopoverPage } from 'src/app/_template/popover/popover.page';
import { ArticleEditComponent } from '../article-edit/article-edit.component';
import { WawiConnectorService } from 'src/app/_provider/wawi-connector/wawi-connector.service';
import { DeviceDetailComponent } from '../device-detail/device-detail.component';
import { DevicesService } from 'src/app/_provider/devices/devices.service';

@Component({
    selector: 'asset-item',
    templateUrl: './asset-item.component.html',
    styleUrls: ['./asset-item.component.scss'],
    standalone: false
})
export class AssetItemComponent implements OnInit {

  @Input() attachment: Attachment
  @Input() canDelete: boolean = true
  @Input() maxWidth350: boolean = false
  @Input() withBorder: boolean = false

  public get serialNumberCount(): number{
    if(this.attachment.type != "Artikel") return 0
    return (this.attachment as AttachmentArticle).serialNumbers.reduce((acc, cur) => acc + cur.quantity, 0) ?? 0
  }

  constructor(
    private filesSrv: FilesService,
    private feedback: FeedbackService,
    private platform: Platform,
    public ticketSrv: TicketEmployee.TicketService,
    private articleSrv: ArticleService,
    private authSrv: AuthService,
    private deviceSrv: DevicesService,
    private wawiConnectorSrc: WawiConnectorService
  ) {}
 
  async ngOnInit() {
    
  }

  public async downloadFile(event: MouseEvent){
    if(this.authSrv.isDemoMode) return await this.feedback.showMsg(MsgType.Info, "In der Demo-Version kann leider keine Datei heruntergeladen werden.🥸")
    
    if(this.attachment.type == "Datei") this.filesSrv.downloadFile(this.attachment)
    else if(this.attachment.type == "Mailanhang"){
      if(!(await this.feedback.confirm("Dieser Anhang stammt aus einer E-Mail. Möchten Sie die E-Mail herunterladen?", "Herunterladen", "Abbrechen"))) return
      await this.filesSrv.downloadFile(this.attachment)
    }
    else if(!this.canDelete) return
    else if(this.attachment.type == "Artikel") await this.openArticle(event, this.attachment.managerId)
    else if(this.attachment.type == "Vorgang") await this.openProcess( this.attachment.managerId)
    else if(this.attachment.type == "Geräte") await this.showDevice(event)
    else await this.feedback.showMsg(MsgType.Info, "Es können nur Dateien heruntergeladen werden. Bitte öffnen Sie dieses Format in der Desktopversion.")
    
  }

  private async openProcess(processId: number){
    if(this.wawiConnectorSrc.connector.executable){
      const connectorResult: HttpFunctionResult = await this.wawiConnectorSrc.connector.openProcess(processId)
      if(connectorResult.success) this.feedback.showInformationToast(`Vorgang wurde in ${this.wawiConnectorSrc.appName} geöffnet.`, "Information", 2000)
      else this.feedback.showError(connectorResult.message)
    }else await this.feedback.showError("Das Öffnen von Vorgängen wird aktuell nur in Verbindung mit der AFS-WaWiToGo oder dem AFS-Webconnector unterstützt.")
  }

  private async openArticle(event: MouseEvent,articleId: number){
    if(this.wawiConnectorSrc.connector.executable){

      const list: PopoverListItem[] = [
        {id: 0, description: `In ${this.wawiConnectorSrc.appName} öffnen`, icon: "wawitogo"},
        {id: 1, description: "Artikel bearbeiten", icon: "fa-pen"}
      ]

      const popoverResult: HttpFunctionResult<PopoverListItem> = await this.feedback.showPopover<PopoverListItem>({
        event, component: PopoverPage, componentProps: {list}
      })

      if(!popoverResult.success) return
      else if(popoverResult.data.id == 0){
        const connectorResult: HttpFunctionResult = await this.wawiConnectorSrc.connector.openArticle(articleId)
        if(connectorResult.success) this.feedback.showInformationToast(`Artikel wurde in ${this.wawiConnectorSrc.appName} geöffnet.`, "Information", 2000)
        else this.feedback.showError(connectorResult.message)
      }else if(popoverResult.data.id == 1) await this.updateArticle()

    }else await this.updateArticle() 
  }

  private async showDevice(event: MouseEvent){
      await this.feedback.showPopover({
        event: event,
        component: DeviceDetailComponent,
        componentProps: {
          attachmentId: this.attachment.id
        }
      })
  }

  private async updateArticle(){
    if(this.attachment.type != "Artikel") return

    if(this.attachment.calculated) return await this.feedback.showInformationToast("Dieser Artikel wurde bereits berechnet und kann nicht bearbeitet werden.")

    const result: HttpFunctionResult<Article> = await this.feedback.showModal<Article>({
      id: "articleEdit",
      component: ArticleEditComponent,
      componentProps: { 
        article: {
          id: this.attachment.managerId,
          description: this.attachment.description,
          toCalculate: this.attachment.toCalculate,
          quantity: this.attachment.quantity,
          serialNumbers: this.attachment.serialNumbers,
          serialNumbersRequired: this.attachment.serialNumbersRequired,
        } as Article,  
        isNew: false}
    })

    if(!result.success) return

    if(this.attachment.type != "Artikel") return

    this.attachment.quantity = result.data.quantity
    this.attachment.toCalculate = result.data.toCalculate
    this.attachment.serialNumbers = result.data?.serialNumbers ?? []
    await this.articleSrv.updateArticleAttachment(this.attachment)

  }

  public async deleteFile(event: any){
    event.stopPropagation()

    let deleteLocally: boolean = false 



    //Nur wenn die Datei lokal gespeichert wurde, es eine Datei ist, es ein natives gerät ist
    if(this.filesSrv.downloadedFiles.find(attachmentName => attachmentName == `${this.attachment.id}.${FilesService.getFileExtention(this.attachment.description)}`) && this.attachment.type == 'Datei' && this.platform.is("capacitor")){
      const result: HttpFunctionResult<AFSActionsheetButton> = await this.feedback.showActionSheet({
        header: "Wo möchten Sie die Datei löschen?",
        buttons: [
          {description: "Lokal löschen", action: () => deleteLocally = true},
          {description: "In Ticket löschen", action: () => deleteLocally = false, danger: true},
        ]
      })
      if(!result.success) return 
    }



    //True = nur lokal löschen
    //False = in Ticket löschen
    if(deleteLocally){
      await this.feedback.updateLoading("Löschen...")
      await this.filesSrv.deleteFileLocally(this.attachment)
      this.filesSrv.downloadedFiles.splice(this.filesSrv.downloadedFiles.indexOf(`${this.attachment.id}.${FilesService.getFileExtention(this.attachment.description)}`), 1)
      this.ticketSrv.ticket.attachments[this.ticketSrv.ticket.attachments.indexOf(this.attachment)] = Object.assign({}, this.attachment) 
      await this.feedback.hideLoading()
    }else{

      if(this.attachment.type == "Artikel")
        if(this.attachment.calculated) return await this.feedback.showInformationToast("Dieser Artikel wurde bereits berechnet und kann nicht gelöscht werden.")

      if(!(await this.feedback.confirm(`Möchten Sie den Anhang ${this.attachment.description} wirklich löschen?`, "Löschen", "Abbrechen"))) return
      await this.feedback.updateLoading("Löschen...")
      const result: HttpFunctionResult<any> = await this.filesSrv.deleteFile(this.attachment)
      await this.feedback.hideLoading()
      if(!result.success) return
      (this.ticketSrv as TicketEmployee.TicketService).removeFile(this.attachment)
    }

  }

}
